import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from './ui/dialog';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import { QrCode, Copy, ExternalLink, Link as LinkIcon } from 'lucide-react';
import { eventRef } from '../utils/eventRef';

const QRScanLinkGenerator = ({ eventId, eventName }) => {
  const [selectedPurpose, setSelectedPurpose] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const { showSuccess, showError } = useToast();

  // Fetch QR scan purposes
  const { data: purposes = [], isLoading: purposesLoading } = useQuery({
    queryKey: ['qrScanPurposes', eventId],
    queryFn: () => ApiService.getQRScanPurposes(eventId),
    enabled: !!eventId,
  });

  const generateLink = () => {
    if (!selectedPurpose) {
      showError('Please select a scan purpose');
      return;
    }

    const baseUrl = window.location.origin;
    const scanUrl = `${baseUrl}/admin/events/${eventRef(eventId)}/scan?purpose=${selectedPurpose}`;
    setGeneratedLink(scanUrl);
  };

  const copyToClipboard = async () => {
    if (!generatedLink) {
      showError('No link generated yet');
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedLink);
      showSuccess('Link copied to clipboard!');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generatedLink;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        showSuccess('Link copied to clipboard!');
      } catch (fallbackError) {
        showError('Failed to copy link. Please copy manually.');
      }
      
      document.body.removeChild(textArea);
    }
  };

  const openLink = () => {
    if (!generatedLink) {
      showError('No link generated yet');
      return;
    }
    window.open(generatedLink, '_blank');
  };

  const resetGenerator = () => {
    setSelectedPurpose('');
    setGeneratedLink('');
  };

  return (
    <>
      <Button 
        variant="outline" 
        className="flex items-center space-x-2"
        onClick={() => setIsOpen(true)}
      >
        <QrCode className="w-4 h-4" />
        <span>Generate QR Scan Link</span>
      </Button>
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <QrCode className="w-5 h-5" />
            <span>Generate QR Scan Link</span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
        
          <div className="space-y-6">
            {/* Event Info */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-medium text-blue-900 mb-1">Event: {eventName}</h3>
              <p className="text-sm text-blue-700">Generate a direct link to the QR scanner with a pre-selected purpose.</p>
            </div>

            {/* Purpose Selection */}
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Select Scan Purpose</Label>
                <p className="text-xs text-gray-500 mb-2">
                  Choose the purpose that will be pre-selected in the QR scanner
                </p>
                <Select value={selectedPurpose} onValueChange={setSelectedPurpose}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a scan purpose..." />
                  </SelectTrigger>
                  <SelectContent>
                    {purposesLoading ? (
                      <SelectItem value="loading" disabled>
                        Loading purposes...
                      </SelectItem>
                    ) : purposes.length === 0 ? (
                      <SelectItem value="none" disabled>
                        No purposes available
                      </SelectItem>
                    ) : (
                      purposes.map((purpose) => (
                        <SelectItem key={purpose.purpose} value={purpose.purpose}>
                          {purpose.name}
                          {purpose.description && (
                            <span className="text-gray-500 ml-2">- {purpose.description}</span>
                          )}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Generate Button */}
              <Button 
                onClick={generateLink}
                disabled={!selectedPurpose || purposesLoading}
                className="w-full"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Generate Link
              </Button>
            </div>

            {/* Generated Link */}
            {generatedLink && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 mb-2">Generated Link:</h4>
                  <div className="bg-white border rounded p-3 font-mono text-sm break-all">
                    {generatedLink}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button onClick={copyToClipboard} className="flex-1">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button onClick={openLink} variant="outline" className="flex-1">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Link
                  </Button>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This link will open the QR scanner with "{purposes.find(p => p.purpose === selectedPurpose)?.name}" purpose pre-selected.
                  </p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={resetGenerator}>
                Reset
              </Button>
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogBody>
      </DialogContent>
      </Dialog>
    </>
  );
};

export default QRScanLinkGenerator;
